<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import { VocabResult, VocabResultDecision } from '../../../types/session';
import localforage from 'localforage';
import { computed, onMounted, ref } from 'vue';
import { StarIcon } from '@heroicons/vue/24/solid';

const { result, type } = defineProps<{ result: VocabResult, type: 'stars' | 'table' }>()
const audioUrl = ref<string>('')
const { t } = useI18n()

const ratting = computed((): { min: number, stars: number, text: string } => {
    const thresholds = [
        { min: 80, stars: 5, text: t(TEXT.outstanding) },
        { min: 40, stars: 4, text: t(TEXT.well_done) },
        { min: 10, stars: 3, text: t(TEXT.need_more_effort) },
    ];
    return thresholds.find(t => result.pronunciation_score >= t.min) || { min: 0, stars: 0, text: t(TEXT.voice_not_recognized) };
})


function getTextColor(decision: VocabResultDecision): string {
    switch (decision) {
        case VocabResultDecision.correct:
            return 'text-lime-500';
        case VocabResultDecision.warning:
            return 'text-yellow-500';
        case VocabResultDecision.incorrect:
            return 'text-orange-600';
        default:
            return '';
    }
}

onMounted(async () => {
    const audioBlob = await localforage.getItem<Blob>(result.key || '')
    audioUrl.value = audioBlob ? URL.createObjectURL(audioBlob) : ''
})

</script>
<template>
    <div class="flex flex-col items-center gap-3 w-full" v-if="type === 'stars'">
        <div class="flex items-center gap-3">
            <p>{{ t(TEXT.score) }}:</p>
            <div class="flex flex-col">
                <div class="flex gap-2 border rounded-full p-3 bg-neutral-100">
                    <div v-for="i in 5">
                        <StarIcon class="size-5 text-gray-300" :class="{ '!text-yellow-500': i <= ratting.stars }"></StarIcon>
                    </div>
                </div>
            </div>
        </div>
        <p class="font-bold mt-3" :class="getTextColor(result.decision)">{{ ratting.text }}</p>
    </div>

    <template v-if="type === 'table'">
        <div class="flex flex-wrap items-center justify-between gap-3 w-full mb-3 p-3">
            <div>
                <h3 class="w-full text-xl">
                    {{ t(TEXT.pronunciation_result) }}:
                    <span class="font-bold ml-3" :class="getTextColor(result.decision)">
                        {{ ratting.text }}
                    </span>
                </h3>
                <h3 class="w-full text-xl">{{ t(TEXT.score) }}:
                    <span class="font-bold ml-3" :class="getTextColor(result.decision)">
                        {{ result.pronunciation_score }}%
                    </span>
                </h3>
            </div>
        </div>
        <div class="p-3">
            <table class="table-auto w-full">
                <thead>
                    <tr>
                        <th class="capitalize">{{ t(TEXT.result) }}</th>
                        <th class="capitalize">{{ t(TEXT.sound) }}</th>
                        <th class="capitalize">{{ t(TEXT.feedback) }}</th>
                    </tr>
                </thead>
                <tbody v-for="word in result.words">
                    <tr v-for="phoneme in word.phonemes">
                        <td class="font-bold text-nowrap" :class="getTextColor(phoneme.decision)">
                            {{ t(TEXT[VocabResultDecision[phoneme.decision]]) }}
                        </td>
                        <td class="font-bold">/{{ phoneme.ipa }}/</td>
                        <td>
                            <ul>
                                <li class="whitespace-pre-wrap" v-for="feedback in phoneme.feedback">
                                    {{ feedback.text }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </template>
</template>

<style lang="postcss" scoped>
thead {
    @apply bg-neutral-200/50
}

tbody {
    @apply bg-white
}

th,
td {
    @apply border-[1px] border-neutral-200 p-2
}
</style>